import React from 'react';
import moment from 'moment';
import { Chip } from '@mui/material';
import { DateFormats } from 'constants/common';
import { ProposalProductsCardTypeChip } from 'modules/desking/ui/components';
import { AftermarketProductCategory, AftermarketProductType } from '@trnsact/trnsact-shared-types';
import { MUITable, MUIBaseEntity, MUITableColumn, MUITableSorting } from 'components/shared/MUITable';

export interface AftermarketProductTableEntity extends MUIBaseEntity {
  id: string;
  vendorName: string;
  productName: string;
  productCost: string;
  createdByName: string;
  createdDateTime: string | number;
  productDescriptionExternal: string;
  status: 'Active' | 'Inactive';
  productType: AftermarketProductType;
  productCategory: AftermarketProductCategory;
}

interface AftermarketProductsTableProps {
  isLoading: boolean;
  sorting: MUITableSorting;
  isPartnerAccount: boolean;
  products: AftermarketProductTableEntity[];
  onProductClick: (productId: string) => void;
}

const createColumns = (isPartnerAccount: boolean): MUITableColumn<AftermarketProductTableEntity>[] => {
  const columns: MUITableColumn<AftermarketProductTableEntity>[] = [
    {
      header: 'Name',
      renderKey: 'productName',
      sortKey: 'productName',
    },
    {
      header: 'Category',
      renderKey: 'productCategory',
      sortKey: 'productCategory',
      renderCell: ({ productCategory, productType }) => (
        <ProposalProductsCardTypeChip productType={productType} productCategory={productCategory} />
      ),
    },
    {
      header: 'Status',
      renderKey: 'status',
      sortKey: 'status',
      renderCell: ({ status }) => (
        <Chip size="small" label={status} color={status === 'Active' ? 'success' : 'default'} />
      ),
    },
    {
      header: 'Description',
      renderKey: 'productDescriptionExternal',
      sortKey: 'productDescriptionExternal',
    },
    {
      header: 'Cost',
      renderKey: 'productCost',
      sortKey: 'productCost',
    },
    {
      header: 'Created Date',
      renderKey: 'createdDateTime',
      sortKey: 'createdDateTime',
      renderCell: ({ createdDateTime }) => moment(createdDateTime).format(DateFormats.DateTimeMedium24Hour),
    },
    {
      header: 'Owned By',
      renderKey: 'createdByName',
      sortKey: 'createdByName',
    },
  ];

  if (!isPartnerAccount) {
    columns.unshift({
      header: 'Vendor',
      renderKey: 'vendorName',
    });
  }

  return columns;
};

export const AftermarketProductsTable = ({
  sorting,
  products,
  isLoading,
  onProductClick,
  isPartnerAccount,
}: AftermarketProductsTableProps) => {
  const columns = createColumns(isPartnerAccount);

  return (
    <MUITable<AftermarketProductTableEntity>
      hover
      stickyHeader
      columns={columns}
      tableData={products}
      isLoading={isLoading}
      sorting={sorting}
      onRowClick={(_, row) => onProductClick(row.id)}
    />
  );
};
